import {
  Box,
  type BoxProps,
  type ConditionalValue,
  Flex,
} from '@chakra-ui/react'
import React, {
  ForwardedRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Virtuoso } from 'react-virtuoso'

import { MenuStyleValues, useMenuStyles } from '../styles/Menu.style'
import { ZIndex } from '../types'
import { PopoverContent } from '../ui/PopoverContent'
import { MenuItem, MenuListItem } from './MenuItem'

const END_REACHED_COOLDOWN_MS = 100

export const enum MenuPlacement {
  BottomLeft = 'bottom-left',
  BottomCenter = 'bottom-center',
  BottomRight = 'bottom-right',
  TopLeft = 'top-left',
  TopRight = 'top-right',
  RightTop = 'right-top',
  RightBottom = 'right-bottom',
  LeftTop = 'left-top',
  LeftBottom = 'left-bottom',
}

export type MenuOptions = {
  width?: ConditionalValue<string>
  maxHeight?: ConditionalValue<string>
  placement?: MenuPlacement
}

type MenuProps<TValue> = {
  items: MenuItem<TValue>[]
  anchorRef?: React.RefObject<HTMLElement>
  onSelect?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  options?: MenuOptions
  header?: React.ReactNode
  headerWrapperVariant?: 'default' | 'divided'
}

export const Menu = <TValue,>({
  items,
  anchorRef,
  onSelect,
  onScrollEndReached,
  options,
  header,
  headerWrapperVariant,
}: MenuProps<TValue>) => {
  const [renderPass, setRenderPass] = useState<number>(0)
  const [endReached, setEndReached] = useState(false)
  const scrollerRef = React.useRef<HTMLElement | null | Window>(null)
  const boxScrollerParentRef = React.useRef<HTMLDivElement>(null)

  const [anchorDimensions, setAnchorDimensions] = useState<{
    height: number
    width: number
  } | null>(null)
  const [boxHeight, setBoxHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (anchorRef?.current) {
      const height = anchorRef.current.clientHeight
      const width = anchorRef.current.clientWidth
      setAnchorDimensions({ height, width })
    }
  }, [anchorRef])

  useLayoutEffect(() => {
    const listContainerRef = (scrollerRef?.current as HTMLElement)?.firstChild
      ?.firstChild as HTMLElement | undefined
    if (listContainerRef) {
      setBoxHeight(listContainerRef.clientHeight)
    }
  }, [renderPass])

  const handleItemSelected = (
    e: React.MouseEvent,
    value: TValue,
    index: number,
    disabled: boolean,
    itemCallback?: (value: TValue, index: number) => void,
  ) => {
    e.stopPropagation()
    if (disabled) return
    onSelect?.(value, index)
    itemCallback?.(value, index)
  }

  const handleScrollEndReachedInternal = () => {
    if (endReached) {
      return
    }

    if (!items || items.length <= 0) {
      return
    }

    onScrollEndReached?.()

    setEndReached(true)
    setTimeout(() => {
      setEndReached(false)
    }, END_REACHED_COOLDOWN_MS)
  }

  let style: Record<string, any> | undefined

  if (anchorRef?.current && anchorDimensions) {
    const anchorElementHeight = anchorDimensions.height
    const anchorElementWidth = anchorDimensions.width

    const yAdjustment = `calc(${MenuStyleValues.anchoredYGap} + ${anchorElementHeight}px)`
    const xAdjustment = `calc(${MenuStyleValues.anchoredXGap} + ${anchorElementWidth}px)`

    style = {
      position: 'absolute',
      zIndex: ZIndex.popover,
      width: options?.width ?? anchorElementWidth,
    }

    const placement = options?.placement ?? MenuPlacement.BottomLeft
    switch (placement) {
      case MenuPlacement.BottomCenter:
        style.top = yAdjustment
        style.left = '50%'
        style.transform = 'translateX(-50%)'
        break
      case MenuPlacement.BottomLeft:
        style.top = yAdjustment
        style.left = 0
        break
      case MenuPlacement.BottomRight:
        style.top = yAdjustment
        style.right = 0
        break
      case MenuPlacement.TopLeft:
        style.bottom = yAdjustment
        style.left = 0
        break
      case MenuPlacement.TopRight:
        style.bottom = yAdjustment
        style.right = 0
        break
      case MenuPlacement.RightTop:
        style.top = 0
        style.left = xAdjustment
        break
      case MenuPlacement.RightBottom:
        style.bottom = 0
        style.left = xAdjustment
        break
      case MenuPlacement.LeftTop:
        style.top = 0
        style.right = xAdjustment
        break
      case MenuPlacement.LeftBottom:
        style.bottom = 0
        style.right = xAdjustment
        break
    }
  } else {
    style = { width: options?.width ?? MenuStyleValues.defaultWidth }
  }

  const listHeight = useRef(0)
  const computedBoxHeight = listHeight.current
    ? `calc(${listHeight.current}px)`
    : '1px'

  useLayoutEffect(() => {
    const el = boxScrollerParentRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const EDGE_PADDING = 8

    if (rect.left < EDGE_PADDING) {
      el.style.left = '0'
      el.style.right = 'auto'
    } else if (rect.right > viewportWidth - EDGE_PADDING) {
      el.style.right = '0'
      el.style.left = 'auto'
    }
  }, [anchorDimensions, items])

  const shouldBeVisible = useMemo(() => {
    return boxHeight || listHeight.current > 0
  }, [boxHeight, renderPass])

  const styles = useMenuStyles()

  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        if (typeof item.content === 'string') return true
        return !item.content.props?.hide
      }),
    [items],
  )

  return (
    <Box css={{ ...styles.container, ...style }} ref={boxScrollerParentRef}>
      {header && (
        <Flex
          css={
            headerWrapperVariant === 'divided'
              ? styles.dividedHeaderContainer
              : styles.headerContainer
          }
        >
          {header}
        </Flex>
      )}

      <Flex
        visibility={shouldBeVisible ? 'visible' : 'hidden'}
        css={styles.scrollerContainer}
        maxHeight={options?.maxHeight ?? MenuStyleValues.defaultMaxHeight}
        height={computedBoxHeight}
      >
        <Virtuoso
          data={visibleItems}
          scrollerRef={(ref) => (scrollerRef.current = ref)}
          style={{
            display: 'flex',
            height: '100%',
          }}
          totalListHeightChanged={(height) => {
            listHeight.current = height
            setRenderPass((prev) => prev + 1)
          }}
          itemContent={(index, data: MenuItem<TValue>) => {
            const displayContent =
              typeof data.content === 'string' ? (
                <MenuListItem primaryText={data.content} />
              ) : (
                data.content
              )

            return (
              <Flex
                css={styles.itemContainer}
                pt={index ? MenuStyleValues.menuItemsGap : '0px'}
                key={index}
                onClick={(e) =>
                  handleItemSelected(
                    e,
                    data.value,
                    index,
                    data.clickDisabled ?? false,
                    data.onSelect,
                  )
                }
              >
                {displayContent}
              </Flex>
            )
          }}
          endReached={handleScrollEndReachedInternal}
          overscan={500}
        />
      </Flex>
    </Box>
  )
}

export const MenuPositioningWrapper = React.forwardRef<
  HTMLDivElement,
  BoxProps
>((props: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
  return (
    <Box ref={ref} position="relative">
      {props.children}
    </Box>
  )
})

export const MenuPopoverWrapper = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ children, ...rest }: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <Flex
        ref={ref}
        zIndex={ZIndex.popover}
        position="relative"
        css={{ ...rest }}
      >
        <PopoverContent width="fit-content" bg="transparent">
          {children}
        </PopoverContent>
      </Flex>
    )
  },
)
